<template>
  <div
    :style="cssVars"
    class="file-content-container mt-10"
  >
    <v-card
      class="login-card"
      data-cy="login-header-card"
      :class="($vuetify.breakpoint.smAndDown) ? 'sm' : ''"
    >
      <v-img
        :src="logo"
        width="206"
        class="mb-5"
        data-cy="logo"
        :style="cssMainLogo"
      />
      <v-avatar
        v-if="isMobile"
        color="#FFFFFF"
        size="80"
        class="avatar-logo"
      >
        <v-img
          :src="logoNoText"
          contain
          max-width="40px"
        />
      </v-avatar>
      <stateful-auth
        v-if="isValidInvite && isValidConsent"
        :user-email="email"
        :init="initKey"
        :invite-guid="inviteGuid"
        :sso-token="ssoToken"
        :redirect-url="redirectUrl"
        :redirect-data="redirectData"
        :session-guid="sessionGuid"
        :consent-client-id="consentClientId"
        :consent-redirect="consentRedirect"
        :consent-state="consentState"
        :init-state="initState"
        auth-context="signingRoom"
        @title="(value) => {titleText = value}"
      />
      <h3
        v-else-if="!isValidInvite"
        style="color: red"
      >
        This invite link is no longer valid.
      </h3>
      <h3
        v-else-if="!isValidConsent"
        style="color: red"
      >
        This consent link is no longer valid.
      </h3>
    </v-card>
  </div>
</template>

<script>
import statefulAuth from '~/components/common/v2/authorization/_statefulAuth.vue'
import logo from '~/assets/images/logos/ncino_logo_color_small.png'
import logoNoText from '~/assets/images/logos/ncino_logo_no_text_color.png'
import { isUserInviteValid } from './graphql/signing_room_queries.gql'
import { apiClient } from './graphql/api_client_queries.gql'

export default {
  name: 'Login',

  components: {
    'stateful-auth': statefulAuth
  },

  data () {
    return {
      state: null,
      email: '',
      logo: logo,
      logoNoText: logoNoText,
      subtitleText: '',
      errorMessages: [],
      inviteGuid: '',
      isValidInvite: true,
      titleText: 'With a little information, we\'ll save your work for you.',
      initKey: 'init',
      initState: 'init',
      ssoToken: '',
      redirectUrl: '',
      redirectData: '',
      sessionGuid: '',
      isValidConsent: true,
      consentClientId: '',
      consentRedirect: '',
      consentState: ''
    }
  },

  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    contentMaxWidth () {
      if (this.$vuetify.breakpoint.mdAndDown) {
        return '100%'
      } else {
        return '560px'
      }
    },
    cssVars () {
      return {
        '--content-max-width': this.contentMaxWidth
      }
    },
    cssMainLogo () {
      if (this.$vuetify.breakpoint.mdAndDown) {
        return 'display: none;'
      } else {
        return ''
      }
    }
  },

  created () {
    if (this.$route && this.$route.query.inviteGuid) {
      this.inviteGuid = this.$route.query.inviteGuid
      this.$apollo.query({
        query: isUserInviteValid,
        variables: {
          inviteGuid: this.inviteGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        const { response } = data.userInvite
        this.isValidInvite = !!response
        this.initKey = 'start'
      }).catch(err => {
        this.isValidInvite = false
        console.log(`Could not find valid invite with ID ${this.inviteGuid}:`, err)
      })
    } else if (this.$route && this.$route.query.ssoToken) {
      sessionStorage.clear()
      this.ssoToken = this.$route.query.ssoToken
      this.initState = 'requestSsoSignIn'
      this.sessionGuid = this.$route.query.sessionGuid ? this.$route.query.sessionGuid : ''
      this.redirectUrl = this.$route.query.redirect_url ? this.$route.query.redirect_url : ''
      this.redirectData = this.$route.query.redirect_data ? this.$route.query.redirect_data : ''
    } else if (this.$route && this.$route.query.prompt && this.$route.query.prompt === 'consent' && this.$route.query.client_id) {
      this.consentClientId = this.$route.query.client_id
      this.consentRedirect = this.$route.query.redirect_uri
      this.consentState = this.$route.query.state
      this.$apollo.query({
        query: apiClient,
        variables: {
          clientId: this.$route.query.client_id
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        const response = data.apiClient
        this.isValidConsent = response != null && response.active
      }).catch(err => {
        this.isValidConsent = false
        console.log(`Could not find valid consent with ID ${this.consentClientId}:`, err)
      })
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~/assets/css/snui';
.file-content-container {
  margin: 0 auto;
  padding: 0 16px;
  max-width: var(--content-max-width);
}
.wrap-overflow {
  overflow-wrap: break-word;
  word-break: break-word;
}
.avatar-logo {
  position: absolute;
  top: -105px;
  left: calc(50% - 40px);
  box-shadow: 0px 1px 1.5px rgba(31, 31, 31, 0.04), 0px 2px 5px rgba(31, 31, 31, 0.1), 0px 3px 9px 6px rgba(31, 31, 31, 0.05)
}
::v-deep .v-avatar .v-image {
  border-radius: 0px;
}
.login-card.sm {
  position: relative;
  top: 10px;
  border: none;
  box-shadow: none;
  width: 100vw;
  padding: 0;
  border-radius: 6px;
}
.login-card:not(.sm) {
  position: relative;
  top: -155px;
  padding: 56px;
  border-radius: 6px;
}

::v-deep .sn-phone .v-input__slot {
  font-family: "Open Sans", sans-serif !important;
  font-size: 16px !important;
  font-weight: 400 !important;
}

::v-deep .sn-phone>:not(div.error--text) .v-input__slot:focus-within fieldset {
  border: 1px solid #1F1F1F !important;
}

::v-deep .v-input--checkbox {
  font-family: "Open Sans", sans-serif !important;
  font-size: 16px !important;
  font-weight: 400 !important;

  .v-icon {
    color: black !important;
  }
}
</style>
